<script>
export default {
  title: 'Project 0: Setting up your environment',
  starterId: 'proj0.zip'
}
</script>

<template>
  <section>
    <article>
      <h2>Submission</h2>
      <p>Nothing needs to be submitted for this project. However, it is strongly suggested
        for you to complete this assignment.</p>
    </article>

    <article>
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above.  It should contains two files:
        <code>hello.c</code> and  <code>eccentric.c</code>.
      </p>
    </article>

    <article>
      <h2 id="introduction">Introduction</h2>

      <p>This project explains how to use your own computer to complete assignments for CS
        9F. If you are using a lab computer, most of the instructions are the same,
        except you won't have to install anything.</p>

      <p>If you need any help at any time, you are welcomed to come in during our regular
        office hour and ask any one of our tutors.</p>
    </article>

    <article>
      <h2>Register for an account</h2>

      <blockquote><p>These accounts allow you to use instructional machines in the CS department,
        which can be useful if you do not have regular access to a computer. They are
        <strong>not required if you do not plan on using the lab computers or printers</strong>.</p></blockquote>

      <p>Go to the <a href="https://acropolis.cs.berkeley.edu/~account/webacct/">EECS account site</a>
        to register for an instructional account. Login using your Berkeley CalNet ID
        and click the <code>Get a new account</code> button in the row for CS 9C. Your username
        will be of the form cs9c-xxx. Write down or download your account form so you
        don't forget it!</p>

      <p>If you decide to use your instructional account to complete your course work, then you can save
        the effort of setting up a local environment on your own computer. You will still need to install
        a terminal, but then you can use SSH to connect to your instructional account and use SFTP to
        transfer files. However, we still recommend you to at least attempt all steps listed below to set
        up your own environment so that you may use it in the future.</p>

    </article>

    <article>
      <h2 id="setup">Setup</h2>

      <blockquote>
        <p>
          In this class, we encourage you <b>NOT</b> to use software like <code>Xcode</code> or
          <code>Visual Studio</code>, which will help you set up the environment. Instead,
          please use a standard terminal and a text editor to complete all your work.
        </p>
      </blockquote>

      <h3 id="install-a-terminal">Install a terminal</h3>

      <p>The terminal is a program that allows you to interact with your computer
        by entering commands. No matter what operating system you use (Windows,
        macOS, Linux), the terminal will be an essential tool for CS 9F.</p>

      <h4 id="macos-linux">macOS/Linux</h4>

      <p>If you're on a Mac or are using a form of Linux (such as Ubuntu), you already
        have a program called <code>Terminal</code> or something similar on your computer.  Open
        that up and you should be good to go.</p>

      <h4 id="windows">Windows</h4>

      <p>For Windows users, we recommend downloading a terminal called <a href="http://git-scm.com/downloads">Git
        Bash</a>.</p>

      <p>You should be able to install Git Bash with most of the default configuration
        options, with one exception. In the <em>Configuring the terminal emulator to use
          with Git Bash</em> step, select the second option: <em>Use Windows' default console
          window</em>.</p>

      <p><strong>This is very important! If you do not select this option, your terminal will
        not work!</strong></p>

      <figure>
        <img class="img-responsive center-block" src="./images/git-bash-options.png" alt="Git Bash configuration options">
      </figure>

      <h3 id="install-gcc">Install gcc</h3>

      <p>gcc is one of the most frequently used C compiler created by GNU
        project. It is widely adopted as the default compiler of UNIX-like systems. Use the instructions
        below based on your system to install the gcc.</p>

      <h4 id="macos">macOS</h4>

      <p>If you are using a Mac, your will need to download and install <code>Command Line Tools for Xcode</code>,
        which can be downloaded in the following ways:</p>

      <ul>
        <li>
          <p>
            <b>Option 1: (Recommended)</b> You can open your terminal and simply type <code>gcc</code>. A popup window should appear, telling you to install the tool.
          </p>

          <figure>
            <img src="./images/terminal-gcc.png" class="img-responsive center-block" width="560px">
          </figure>
          <p>
            Alternatively, you can use the following command to install Xcode Command Line Tools. It
            will produce a similar alert box. Note the double hyphen:
          </p>
          <pre v-highlightjs><code class="bash">$ xcode-select --install</code></pre>
        </li>
        <li>
          <p><b>Option 2:</b> Install Xcode tools using Xcode app on the Mac:</p>
          <ol type="1">
            <li>Start Xcode on the Mac.</li>
            <li>Choose Preferences from the Xcode menu.</li>
            <li>In the General panel, click Downloads.</li>
            <li>On the Downloads window, choose the Components tab.</li>
            <li>Click the Install button next to Command Line Tools.</li>
            <li>You are asked for your Apple Developer login during the install process.</li>
          </ol>
          <p></p>
        </li>
        <li>
          <p><b>Option 3:</b> Install the necessary Xcode tools from the Web:</p>
          <ol type="1">
            <li>On the Mac, go to <a href="https://developer.apple.com/downloads/index.action" target="_blank">https://developer.apple.com/downloads/index.action</a></li>
            <li>You are asked for your Apple Developer login during the install process.</li>
            <li>On the "Downloads for Apple Developers" list, select the Command Line Tools entry that you want.</li>
          </ol>
          <p></p>
        </li>
      </ul>

      <h4>Windows</h4>

      <p>To install gcc, the trick is to install MinGW installation manager.</p>

      <ol>
        <li>
          <p>Go to MinGW <a href="http://sourceforge.net/projects/mingw/">SourceForge</a> and download the file.</p>
        </li>
        <li>
          <p>Follow the instruction to install it.</p>
        </li>

        <li>
          <p>When you see <code>MinGW Installation Manager Dialog</code>, click on <strong>MSYS</strong> in the left sidebar, you will see <code>mingw-developer-toolkit</code> in the right side panel. Now just right click on it and select <code>mark for installation</code>.</p>

          <figure>
            <img class="img-responsive center-block" src="./images/minGW_manager.png" alt="dialog" width="560">
          </figure>
        </li>
        <li>
          <p>In the navigation bar, select <strong>Installation &gt; Apply Changes</strong>. A new dialog will appear,
            you want to click on <strong>Apply</strong>.</p>

          <p>Similarly, navigate to the <b>MinGW Base System</b> and <b>MSYS Base System</b> tabs in the left sidebar
            and select everything related to gcc.</p>

          <figure>
            <img class="img-responsive center-block" src="./images/apply_changes.png" width="560">
          </figure>

          <p>The last step is to go to Windows environment variable settings: <strong>Control Panel &gt; System and
            Security &gt; System</strong>.</p>

          <figure>
            <img class="img-responsive center-block" src="./images/windows_var.png" width="560">
          </figure>
        </li>
        <li>
          <p>Click on <strong>Advanced system settings</strong>.</p>

          <figure>
            <img class="img-responsive center-block" src="./images/windows_PATH.png" width="560">
          </figure>
        </li>
        <li>
          <p>Click on <strong>Environment Variables</strong>.</p>

          <figure>
            <img class="img-responsive center-block" src="./images/windows_env_var.png" width="560">
          </figure>
        </li>
        <li>
          <p>Double Click on <strong>System variables &gt; Path</strong>. Put <code class="highlighter-rouge">;C:\MinGW\bin\</code> into the <strong>Variable value</strong>. <code class="highlighter-rouge">;</code> is the seperator.</p>

          <figure>
            <img class="img-responsive center-block" src="./images/windows_edit_var.png" width="560">
          </figure>
        </li>
      </ol>

      <h3 id="install-a-text-editor">Install a text editor</h3>

      <p>The <strong>C Compiler</strong> that you just installed allows you to <em>compile</em> and <em>run</em> C
        code. You will also need a <strong>text editor</strong>, where you will <em>write</em> C code.</p>

      <p>There are many editors out there, each with its own set of features. We find
        that <a href="https://atom.io/">Atom</a> and <a href="http://www.sublimetext.com/3">Sublime Text 3</a> are popular choices among students,
        but you are free to use other text editors.</p>

      <blockquote><p><strong>Note</strong>: Please, please, <em>please</em> do not use word processors such as
        Microsoft Word to edit programs.</p></blockquote>

      <p>For your reference, we've also written some guides on using popular text
        editors.</p>

      <ul>
        <li><a href="https://cs61a.org/articles/atom.html" target="_blank">Atom</a></li>
        <li><a href="https://cs61a.org/articles/sublime.html" target="_blank">Sublime Text 3</a></li>
        <li><a href="https://cs61a.org/articles/emacs.html" target="_blank">Emacs</a></li>
        <li><a href="https://cs61a.org/articles/vim.html" target="_blank">Vim</a></li>
      </ul>

      <p>
        In the future, when you start to build more complex projects, you may find IDEs, such as Xcode, Visual Studio, Clion, become
        more useful. However, they are not recommended for this course.
      </p>

      <h2 id="using-the-terminal">Using the terminal</h2>

      <p>Let's check if everything was installed properly! First, open a new terminal window,
        if you haven't already.</p>

      <figure>
        <img class="img-responsive center-block" src="./images/terminal.png" alt="starting the terminal">
      </figure>

      <p>When you first open your terminal, you will start in the home directory.  The
        <strong>home directory</strong> is represented by the <code>~</code> symbol.</p>

      <blockquote><p>Don't worry if your terminal window doesn't look exactly the same; the
        important part is that the text on the left-hand side of the <code>$</code> has a
        <code>~</code> (tilde). That text might also have the name of your computer.</p></blockquote>
    </article>

    <article>
      <h2 id="organizing-your-files">Organizing your files</h2>

      <p>In this section, you will learn how to manage files using terminal commands.</p>

      <h3 id="directories">Directories</h3>

      <p>The first command you'll use is <code>ls</code>. Try typing it in the terminal:</p>

      <pre v-highlightjs><code class="bash">ls</code></pre>

      <p>The <code>ls</code> command <strong>l</strong>i<strong>s</strong>ts all the files and folders in the current
        directory. A <strong>directory</strong> is another name for a folder (such as the
        <code>Documents</code> folder). Since you're in the home directory right now, you
        should see the contents of your home directory.</p>

      <h3 id="changing-directories">Changing directories</h3>

      <p>To move into another directory, use the <code>cd</code> command. Let's try moving into your
        Desktop directory. First, make sure you're in your home directory (check for the
        <code>~</code> on your command line) and use <code>ls</code> to see if the <code>Desktop</code> directory is present.
        Try typing the following command into your terminal, which should move you into
        that directory:</p>

      <pre v-highlightjs><code class="bash">cd Desktop</code></pre>

      <p>If your desktop directory is not located within your home directory and you can't
        find it, ask a tutor for help.</p>

      <p>There are a few ways to return to the home directory:</p>

      <ul>
        <li><code>cd ..</code> (two dots). The <code>..</code> means "the parent directory". In
          this case, the parent directory of <code>cs9c</code> is your home
          directory, so you can use <code>cd ..</code> to go up one directory.</li>
        <li><code>cd ~</code> (the tilde). Remember that <code>~</code> means home directory, so
          this command will always change to your home directory.</li>
        <li><code>cd</code> (<code>cd</code> on its own). Typing just <code>cd</code> is a shortcut for typing <code>cd ~</code>.</li>
      </ul>

      <blockquote><p>You do not have to keep your files on your Desktop if you prefer otherwise.
        Where you keep your files locally will not affect your projects. Do whatever is
        easiest and most convenient for you!</p></blockquote>

      <h3 id="making-new-directories">Making new directories</h3>

      <p>The next command is called <code>mkdir</code>, which <strong>m</strong>a<strong>k</strong>es new
        <strong>dir</strong>ectories. Let's make a directory called <code>cs9c</code> on your Desktop to store
        all of the assignments for this class:</p>

      <pre v-highlightjs><code class="bash">mkdir cs9c</code></pre>

      <p>A folder named <code>cs9c</code> will appear on your Desktop. You can verify this by
        using the <code>ls</code> command again or by simply checking your Desktop.</p>

      <p>At this point, let's create some more directories. First, make sure you are in
        the <code>~/Desktop/cs9c</code> directory. Then, create a folder called <code>projects</code>
        inside of your <code>cs9c</code> folder:</p>

      <pre v-highlightjs><code class="bash">cd ~/Desktop/cs9c
mkdir projects</code></pre>

      <p>Now if you list the contents of the directory (using <code>ls</code>), you'll see the
        folder you just created, <code>projects</code>.</p>

      <figure>
        <img class="img-responsive center-block" src="./images/terminal_commands.png" alt="cs9c directory">
      </figure>

      <h3 id="downloading-the-assignment">Downloading the assignment</h3>

      <p>If you haven't already, download the zip archive, <code>proj0.zip</code>, which
        contains all the files that you'll need for this project. Once you've done that, let's
        find the downloaded file. On most computers, <code>proj0.zip</code> is probably located in a
        directory called <code>Downloads</code> in your home directory. Use the <code>ls</code> command to
        check:</p>

      <pre v-highlightjs><code class="bash">ls ~/Downloads</code></pre>

      <p>If you don't see <code>proj0.zip</code>, ask a tutor for help.</p>

      <h3 id="extracting-starter-files">Extracting starter files</h3>

      <p>You must expand the zip archive before you can work on the project files.
        Different operating systems and different browsers have different ways of
        unzipping. If you don't know how, you can search online.</p>

      <blockquote><p>Using a terminal, you can unzip the zip file from the command line.
        First, <code>cd</code> into the directory that contains the zip file:</p>

        <pre v-highlightjs><code class="bash">cd ~/Downloads</code></pre>

        <p>Now, run the <code>unzip</code> command with the name of the zip file:</p>

        <pre v-highlightjs><code class="bash">unzip proj0.zip</code></pre>

        <p>You might also be able to unzip files without using the terminal by double
          clicking them in your OS's file explorer.</p></blockquote>

      <p>Once you unzip <code>proj0.zip</code>, you'll have a new folder called <code>proj0</code> which
        contains the following files (check it out with <code>cd</code> and <code>ls</code>):</p>

      <ul>
        <li><code>hello.c</code>: A sample C hello world program for you to learn how to run it</li>
        <li><code>eccentric.c</code>: The template file you'll be adding your code to</li>
      </ul>

      <h3 id="moving-files">Moving files</h3>

      <p>Move the project files to the project folder you created earlier:</p>

      <pre v-highlightjs><code class="bash">mv ~/Downloads/proj0 ~/Desktop/cs9c/projects</code></pre>

      <p>The <code>mv</code> command will <strong>m</strong>o<strong>v</strong>e the <code>~/Downloads/proj0</code> folder
        into the <code>~/Desktop/cs9c/projects</code> folder.</p>

      <p>Now, go to the <code>proj0</code> folder that you just moved. Try using <code>cd</code> to navigate
        your own way! If you get stuck, you can use the following command:</p>

      <pre v-highlightjs><code class="bash">cd ~/Desktop/cs9c/projects/proj0</code></pre>

      <h3 id="summary">Summary</h3>

      <p>Here is a summary of the commands we just went over for your reference:</p>

      <ul>
        <li><code>ls</code>: <strong>l</strong>i<strong>s</strong>ts all files in the current directory</li>
        <li><code>cd &lt;path to directory&gt;</code>: <strong>c</strong>hange into the specified <strong>d</strong>irectory</li>
        <li><code>mkdir &lt;directory name&gt;</code>: <strong>m</strong>a<strong>k</strong>e a new <strong>dir</strong>ectory with the given name</li>
        <li><code>mv &lt;source path&gt; &lt;destination path&gt;</code>: <strong>m</strong>o<strong>v</strong>e the file at the given source to the
          given destination</li>
      </ul>

      <p>Finally, you're ready to start editing the project files! Don't worry if this seems
        complicated -- it will get much easier over time. Just keep practicing!</p>
    </article>

    <article>
      <h2 id="doing-the-assignment">Doing the assignment</h2>

      <article>
        <h4>Compiling and Running a C Program</h4>

        <p>
          In this class, we will be using the command line program gcc to compile programs in C.
          The simplest way to run gcc is as follows:
        </p>

        <pre v-highlightjs><code class="bash">$ gcc hello.c</code></pre>

        <p>
          This compiles <code>hello.c</code> into an executable file named <code>a.out</code>.
          If you've taken CS61B or have experience with Java, you can kinda think of <code>gcc</code> as the
          C equivalent of <code>javac</code>. This file can be run with the following command:
        </p>

        <pre v-highlightjs><code class="bash">$ ./a.out</code></pre>

        <p>
          The executable file is <code>a.out</code>, so what is the <code>./</code>? Answer: when you want to execute an
          executable, you need to prepend a filepath in order to distinguish your command from
          a command like python3. The dot refers to the "current directory." Incidentally, double
          dots (<code>..</code>) would refer to the directory one level up.
        </p>

        <p>
          gcc has various command line options which you are encouraged to explore. In this assignment,
          however, we will only be using <code>-o</code>, which is used to specify the name of the executable file that gcc creates,
          <code>-Wall</code>, which means "give all relevant warnings", and <code>-g</code>, which storing information in the executable file for use by the debugger
          Using the following command, you would use the following commands to compile <code>hello.c</code> into a program named hello, and then
          run it. This is helpful if you don't want all of your executable files to be named <code>a.out</code>.
        </p>

        <pre v-highlightjs><code class="bash">$ gcc -g -Wall -o hello hello.c
$ ./hello</code></pre>
      </article>

      <article>
        <h4>Understanding the question</h4>

        <p>
          In this exercise, we will see an example of preprocessor macro definitions. Macros can be a messy topic,
          but in general the way they work is that before a C file is compiled, all macro constant names are replaced
          exactly with the value they refer to.
        </p>

        <p>
          In the scope of this exercise, we will be using macro definitions exclusively as global constants. Here we
          define CONSTANT_NAME to refer to literal_value (an integer literal). Note that there is only a space
          separating name from value.
        </p>

        <pre v-highlightjs><code class="c">#define CONSTANT_NAME literal_value</code></pre>

        <p>
          Now, look at the code contained in <code>eccentric.c</code>. Notice the four different examples
          of basic C control flow. (What are they?)
        </p>

        <p>
          First compile and run the program to see what it does. Play around with the constant values of the
          four macros: <code>V0</code> through <code>V3</code>. See how changing each of them changes the
          program output.
        </p>

        <p>
          Your task: Modifying only these four values, make the program produce the following output.
        </p>

        <pre v-highlightjs><code class="bash">$ gcc -g -Wall -o eccentric eccentric.c
$ ./eccentric
Berkeley eccentrics:
====================
Happy Happy Happy
Yoshua

Go BEARS!</code></pre>

        <p>
          There are actually several different combinations of macros that can give this output. Here's
          the challenge for you in this exercise: consider what the minimum number of distinct values
          that <code>V0</code> through <code>V3</code> can have such that they still give this exact output. For example, the
          theoretical maximum is four, when they are all distinct from each other.
        </p>

      </article>

    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/c/Setup.vue